import type { BulkImportReport } from "../types";
import type { CsvParseResult } from "../utils/csvPreview";
import { CheckCircle2, AlertTriangle, XCircle, Info, Table2 } from "lucide-react";
import { useState } from "react";
import { useLocale } from "../../../context/LanguageContext";

interface RosterDryRunPreviewProps {
  report: BulkImportReport;
  fileName: string;
  csvPreview: CsvParseResult | null;
  onConfirm: () => void;
  onCancel: () => void;
  isImporting: boolean;
}

export function RosterDryRunPreview({
  report,
  fileName,
  csvPreview,
  onConfirm,
  onCancel,
  isImporting,
}: RosterDryRunPreviewProps) {
  const totalRows = report.successCount + report.skippedCount + report.errorCount;
  const [showPreview, setShowPreview] = useState(true);
  const { t } = useLocale();

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t("roster.dryRunTitle", { fileName })}
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t("roster.dryRunSubtitle", { count: String(totalRows) })}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-3 border-b border-border px-4 py-3 sm:grid-cols-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <span className="text-sm text-foreground">
            {t("roster.willBeImported", { count: String(report.successCount) })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <span className="text-sm text-foreground">
            {t("roster.willBeSkipped", { count: String(report.skippedCount) })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-destructive" />
          <span className="text-sm text-foreground">
            {t("roster.dryRunErrors", { count: String(report.errorCount) })}
          </span>
        </div>
      </div>

      {/* Warnings */}
      {report.warnings.length > 0 && (
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-start gap-2 rounded-md bg-warning/10 p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div className="text-xs text-foreground">
              {report.warnings.map((w, i) => (
                <p key={i}>{w}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Errors Table */}
      {report.errors.length > 0 && (
        <div className="max-h-48 overflow-y-auto border-b border-border px-4 py-3">
          <p className="mb-2 text-xs font-medium text-destructive">
            {t("roster.rowErrorsTitle")}
          </p>
          <div className="space-y-1">
            {report.errors.slice(0, 50).map((err, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-xs text-muted-foreground"
              >
                <span className="shrink-0 font-mono text-destructive">
                  Row {err.row}:
                </span>
                <span>{err.error}</span>
              </div>
            ))}
            {report.errors.length > 50 && (
              <p className="text-xs text-muted-foreground">
                {t("roster.andMoreErrors", { count: String(report.errors.length - 50) })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Data Preview Table */}
      {csvPreview && csvPreview.rows.length > 0 && (
        <div className="border-b border-border">
          <button
            type="button"
            onClick={() => setShowPreview((p) => !p)}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50"
          >
            <Table2 className="h-3.5 w-3.5" />
            {showPreview ? t("roster.hideDataPreview") : t("roster.showDataPreview")}
            <span className="ml-auto text-[10px] tabular-nums">
              {t("roster.showingRows", { shown: String(csvPreview.rows.length), total: String(csvPreview.totalRows) })}
            </span>
          </button>

          {showPreview && (
            <div className="max-h-72 overflow-auto px-4 pb-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="sticky top-0 z-10 bg-muted/80 px-2 py-1.5 text-left font-medium text-muted-foreground backdrop-blur-sm">
                      #
                    </th>
                    {csvPreview.headers.map((header) => (
                      <th
                        key={header}
                        className="sticky top-0 z-10 bg-muted/80 px-2 py-1.5 text-left font-medium text-muted-foreground backdrop-blur-sm"
                      >
                        {header}
                      </th>
                    ))}
                    <th className="sticky top-0 z-10 bg-muted/80 px-2 py-1.5 text-left font-medium text-muted-foreground backdrop-blur-sm">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.rows.map((row) => {
                    // Highlight rows that have errors
                    const rowError = report.errors.find(
                      (e) => e.row === row.rowNumber
                    );
                    const isSkipped = report.skippedRows.includes(row.rowNumber);
                    const rowStatus = rowError
                      ? "error"
                      : isSkipped
                        ? "skipped"
                        : "imported";
                    return (
                      <tr
                        key={row.rowNumber}
                        className={`border-b border-border/50 ${
                          rowError
                            ? "bg-destructive/5"
                            : isSkipped
                              ? "bg-warning/5"
                              : "hover:bg-muted/20"
                        }`}
                        title={rowError ? `Error: ${rowError.error}` : undefined}
                      >
                        <td className="px-2 py-1 font-mono text-muted-foreground">
                          {row.rowNumber}
                        </td>
                        {csvPreview.headers.map((header) => (
                          <td
                            key={header}
                            className="max-w-[200px] truncate px-2 py-1 text-foreground"
                            title={String(row[header] ?? "")}
                          >
                            {row[header] || (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </td>
                        ))}
                        <td className="px-2 py-1">
                          {rowStatus === "error" ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
                              <XCircle className="h-3 w-3" /> {t("roster.rowError2")}
                            </span>
                          ) : rowStatus === "skipped" ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-medium text-warning">
                              <AlertTriangle className="h-3 w-3" /> {t("roster.rowWillSkip")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
                              <CheckCircle2 className="h-3 w-3" /> {t("roster.rowWillImport")}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isImporting}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isImporting || report.successCount === 0}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isImporting ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              {t("roster.importing")}
            </span>
          ) : (
            t("roster.importButton", { count: String(report.successCount) })
          )}
        </button>
      </div>
    </div>
  );
}
